<template>
  <ToolShell
    class="rate-calculator"
    tool-name="Hourly rate calculator"
    h1="Hourly rate calculator for freelancers and agencies."
    lede="Your hourly rate is the revenue you need — your income goal plus business expenses, with a buffer — divided by the hours you can actually bill. This calculator works it out, with a day rate, free and with no signup."
    :trust="['No signup', 'Instant results', 'Hourly and day rate']"
    form-title="Your numbers"
    :upsell-points="[
      'Every proposal and invoice attached to the client record',
      'Payment links and follow-up reminders built in',
      'Reporting built in for clients and projects',
    ]"
    free-label="The free calculator"
    :free-bullets="[
      'An hourly and day rate calculated once, from your inputs',
      'You re-check the math manually as costs change',
      'Nothing connects the rate to an actual proposal',
    ]"
    upgrade-headline="This number works once. Workspace369 makes it a workflow."
    :connected-bullets="[
      'Proposals and invoices go out from the client record',
      'Payment links collect online; payment plans start on Specialist',
      'Follow-up reminders run on a schedule with automations',
      'Everything lands on the client record — status always visible',
    ]"
    slug="/tools/hourly-rate-calculator"
    :meta-description="metaDescription"
    doc-brand="Hourly rate calculator"
    doc-subtitle="Rate estimate from your planning assumptions"
    doc-kind="SUMMARY"
    stamp="Result"
  >
    <template #form>
      <label class="ta-field">
        <span>Target annual income before personal taxes ($)</span>
        <div class="ta-input">
          <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1.5v13M11.5 4.5c-.6-1.2-1.9-1.8-3.5-1.8-1.9 0-3.2.9-3.2 2.4 0 3.4 7 1.7 7 5.1 0 1.6-1.5 2.5-3.8 2.5-1.8 0-3.2-.8-3.8-2" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>
          <input v-model.number="form.income" type="number" min="0" step="1000" placeholder="120000" />
        </div>
      </label>

      <label class="ta-field">
        <span>Billable hours per week</span>
        <div class="ta-input">
          <svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M8 5v3.2l2.2 1.6" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>
          <input v-model.number="form.hours" type="number" min="0.01" max="168" step="any" placeholder="25" />
        </div>
      </label>

      <label class="ta-field">
        <span>Working weeks per year</span>
        <div class="ta-input">
          <svg viewBox="0 0 16 16" aria-hidden="true"><rect x="2" y="3" width="12" height="11" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M2 6.5h12M5.5 1.5v3M10.5 1.5v3" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>
          <input v-model.number="form.weeks" type="number" min="0.01" max="52" step="any" placeholder="46" />
        </div>
      </label>

      <label class="ta-field">
        <span>Monthly business expenses ($, optional)</span>
        <div class="ta-input">
          <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2.5 6.5 8 3l5.5 3.5v5L8 15l-5.5-3.5v-5zM8 7.5V15M2.5 6.5 8 10l5.5-3.5" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>
          <input v-model.number="form.expenses" type="number" min="0" step="50" placeholder="800" />
        </div>
      </label>

      <label class="ta-field">
        <span>Revenue buffer (%, optional)</span>
        <div class="ta-input">
          <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M12.5 3.5l-9 9" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/><circle cx="4.8" cy="4.8" r="1.8" fill="none" stroke="currentColor" stroke-width="1.4"/><circle cx="11.2" cy="11.2" r="1.8" fill="none" stroke="currentColor" stroke-width="1.4"/></svg>
          <input v-model.number="form.margin" type="number" min="0" max="99.99" step="0.01" placeholder="20" />
        </div>
      </label>
      <p class="rate-help">The filled values are editable examples, not typical freelancer averages. Enter an income goal, more than zero and at most 168 billable hours per week, and more than zero and at most 52 working weeks per year. Blank optional expenses and buffer count as zero; the buffer must be between 0% and 99.99%.</p>
      <p v-show="!result" class="rate-help" role="status">Enter valid planning inputs to see a rate. A dash means the estimate is unavailable.</p>
    </template>

    <template #preview>
      <div class="ta-doc-meta">
        <div><span>Revenue needed / year</span><strong>{{ money(result?.needed) }}</strong></div>
        <div><span>Billable hours / year</span><strong>{{ number(result?.yearlyHours) }}</strong></div>
        <div><span>Base cost per hour</span><strong>{{ money(result?.baseRate) }}</strong></div>
        <div><span>Day rate (8h)</span><strong>{{ money(result?.daily) }}</strong></div>
      </div>

      <div class="ta-table-scroll" tabindex="0">
        <table class="ta-table">
          <thead>
            <tr><th>Your inputs</th><th>Value</th></tr>
          </thead>
          <tbody>
            <tr>
              <td>Target annual income</td>
              <td class="ta-num">{{ money(result?.income) }}</td>
            </tr>
            <tr>
              <td>Annual business expenses</td>
              <td class="ta-num">{{ money(result?.annualExpenses) }}</td>
            </tr>
            <tr>
              <td>Revenue buffer</td>
              <td class="ta-num">{{ percent(result?.margin) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="ta-doc-total">
        <span>Hourly rate</span>
        <strong>{{ money(result?.hourly) }}</strong>
      </div>

      <p class="ta-doc-note">Annual billable hours = billable hours per week × working weeks. The day rate assumes eight billable hours. This is a cost-based planning estimate, not a market rate or a personal tax calculation.</p>
    </template>

    <template #steps>
      <div><span>01</span><div><h2>Set the income goal</h2><p>Your annual income goal before personal taxes, separate from business expenses.</p></div></div>
      <div><span>02</span><div><h2>Add hours and costs</h2><p>Use your own billable hours, working weeks and costs; the buffer is a share of modeled revenue.</p></div></div>
      <div><span>03</span><div><h2>Read hourly and day rate</h2><p>Both rates update live, with the revenue and cost breakdown behind them.</p></div></div>
    </template>

    <section class="tool-faq">
      <div class="tool-faq-inner">
        <h2>Rate calculator FAQ</h2>
        <div class="tool-faq-list">
          <details v-for="item in faq" :key="item.q" class="tool-faq-item">
            <summary>{{ item.q }}</summary>
            <p>{{ item.a }}</p>
          </details>
        </div>
      </div>
    </section>

    <section class="rate-context">
      <h2>How does the hourly-rate model work?</h2>
      <p>Annual revenue needed = (annual income goal + monthly business expenses × 12) ÷ (1 − revenue buffer ÷ 100). Divide that revenue by annual billable hours for the hourly rate. The buffer is a share of final modeled revenue, not a markup added to costs.</p>
      <p>For example, a $120,000 income goal plus $800 monthly expenses gives a $129,600 annual cost base. With a 20% revenue buffer and 25 billable hours for 46 weeks, revenue needed is $162,000 and the hourly rate is about $140.87. These inputs are illustrative assumptions, not observed averages.</p>
      <p>Keep sales, administration, leave and other non-billable time out of your billable-hours estimate. Avoid counting expenses twice. A quoted eight-hour day differs from a day with fewer billable hours; agree the included hours and scope with the client.</p>
      <p>For external context, <NuxtLink :to="localePath('/research/freelancer-rate-benchmarks')">compare published freelancer rates by market and unit</NuxtLink>. UK day rates and US hourly rates are different populations and units, not interchangeable prices. For billing-model choices, read <NuxtLink :to="localePath('/blog/fixed-fee-vs-hourly-vs-retainer-billing')">fixed-fee, hourly and retainer billing</NuxtLink>.</p>
    </section>

    <RelatedPages path="/tools/hourly-rate-calculator" title="Hourly rate calculator" />

    <Cta03 />
  </ToolShell>
</template>

<script setup>
import ToolShell from "~/components/tools/ToolShell.vue";

import { calculateHourlyRate, planningMoney as money, planningNumber as number, planningPercent as percent } from "~/utils/rate-planning.mjs";
const { localePath } = useWorkspaceLocale();
const form = reactive({ income: 120000, hours: 25, weeks: 46, expenses: 800, margin: 20 });
const result = computed(() => calculateHourlyRate(form));

const faq = [
  {
    q: "How do I calculate my hourly rate as a freelancer?",
    a: "Add your annual income goal and annual business expenses, then divide by one minus your revenue buffer as a decimal. Divide that required revenue by your expected annual billable hours. The buffer is not a personal tax rate or a markup on costs.",
  },
  {
    q: "How many hours are actually billable?",
    a: "Use your own expected billable hours and working weeks. Exclude sales, administration, leave and unbilled gaps. The filled values are examples, not measured averages for freelancers.",
  },
  {
    q: "Should I use the hourly or day rate?",
    a: "This calculator multiplies the hourly estimate by eight billable hours. If the client agrees to a different day length or deliverable scope, adjust the price accordingly; the displayed day rate does not establish a market price.",
  },
];

const metaTitle = "Hourly Rate Calculator | Workspace369 Free Tools";
const metaDescription =
  "Calculate the hourly and day rate you need from income goals, billable hours, expenses, and margin. Free freelancer rate calculator — instant, no signup.";

// /embed/tools/ renders this page without its FAQ, so no FAQ schema there.
const embed = inject("toolEmbed", false);

useHead(() => ({
  title: metaTitle,
  meta: [
    { name: "description", content: metaDescription },
    { name: "twitter:title", content: metaTitle },
    { name: "twitter:description", content: metaDescription },
    { name: "twitter:card", content: "summary_large_image" },
    { property: "og:title", content: metaTitle },
    { property: "og:description", content: metaDescription },
  ],
  script: embed ? [] : [
    {
      key: "tool-faq-schema",
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faq.map((item) => ({
          "@type": "Question",
          name: item.q,
          acceptedAnswer: { "@type": "Answer", text: item.a },
        })),
      }),
    },
  ],
}));
</script>

<style scoped>
.rate-calculator :deep(.ta-grid > *) { min-width: 0; }
.rate-calculator :deep(.ta-doc-letterhead) { flex-wrap: wrap; }
.rate-calculator :deep(.ta-doc-brandtext) { flex: 1 1 10rem; }
.rate-calculator :deep(.ta-doc-brandtext strong),
.rate-calculator :deep(.ta-doc-brandtext span) { white-space: normal; overflow-wrap: anywhere; }
.rate-calculator :deep(.ta-doc-stamp) { display: none; }
.rate-calculator :deep(.ta-doc-total) { flex-wrap: wrap; }
.rate-calculator :deep(.ta-doc-total span) { flex: 1 1 10rem; }
.rate-calculator :deep(.ta-doc-total strong),
.rate-calculator :deep(.ta-doc-meta > *) { min-width: 0; max-width: 100%; overflow-wrap: anywhere; }
.rate-calculator :deep(.ta-table) { table-layout: fixed; }
.rate-calculator :deep(.ta-table th),
.rate-calculator :deep(.ta-table td) { overflow-wrap: anywhere; white-space: normal; }
.rate-help { color: #475569; font-size: 0.875rem; line-height: 1.6; margin-top: 1rem; }
.rate-context { max-width: 72rem; margin: 0 auto; padding: 3rem 1.25rem; color: #334155; }
.rate-context h2 { font-size: 1.5rem; font-weight: 600; margin-bottom: 1rem; }
.rate-context p { max-width: 70ch; line-height: 1.75; margin-bottom: 1.25rem; }
.rate-context a { text-decoration: underline; text-underline-offset: 0.2em; }

/* Dark mode: the site's dark tokens (page #020617, card #0f172a, hairline #243247, text #f1f5f9 / #cbd5e1 / #94a3b8, accent #67e8f9). Light styles above are unchanged. */
.dark .rate-help {
  color: #cbd5e1;
}

.dark .rate-context {
  color: #f1f5f9;
}


</style>
